import React, { useCallback, useEffect, useMemo, useState, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import {
  formatAdminDashboardSummary,
  pickAdminDashboardLocaleText,
} from "../../shared/locale/admin-dashboard-copy";
import { formatAdminHashedAccountCueLabel } from "../../shared/formatting/format-admin-account-identity";
import { formatAdminAuthModeLabel } from "../../shared/locale/admin-dashboard-status-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import {
  createDefaultAdminListTimeRange,
  parseDeviceKpiQueryParams,
} from "../../contracts/admin-route-params";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import { AdminSourceStatusGrid } from "../../shared/ui/admin-source-status-grid";
import {
  buildAdminStateRouteMeta,
  buildAdminStateScopeMeta,
  buildAdminStateTimeRangeMeta,
} from "../../shared/ui/admin-screen-state-meta";
import { AdminStatusBadge } from "../../shared/ui/admin-status-badge";
import {
  clearHiddenAdminListCursorSearchParams,
  clearExplicitAdminTimeRangeSearchParams,
  resolveExplicitAdminTimeRangeUpperBound,
  resolveAdminLiveTimeRangeRefreshSeed,
  useAdminAutoRefreshTick,
} from "../../shared/query/admin-auto-refresh";
import { AdminLiveRangeBanner } from "../../shared/ui/admin-live-range-banner";
import { useAdminManualRefreshRegistration } from "../../shared/query/admin-manual-refresh-context";
import { useDeviceKpiScreenModel } from "./use-device-kpi-screen-model";
import {
  pickDefaultDeviceKpiScenarioPresetId,
  type DeviceKpiScenarioPresetId,
  type DeviceKpiScenarioPresetViewModel,
} from "./device-kpi-view-model";

export type DeviceKpiScreenProps = {
  dependencies: AdminDashboardDependencies;
};

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

function baseControlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

function createNextSearchParams(formData: FormData, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of ["from", "to", "release", "appVersion", "platform", "deviceInstallId"]) {
    const rawValue = formData.get(key);
    const value = typeof rawValue === "string" ? rawValue.trim() : "";

    if (value.length > 0) {
      params.set(key, value);
    }
  }

  return withAdminDashboardLocaleSearchParams(params, locale);
}

function formatScopeSummary(
  params: URLSearchParams,
  locale: "ko" | "en",
): string | undefined {
  return formatAdminDashboardSummary(locale, [
    { key: "device", value: params.get("deviceInstallId") },
    { key: "release", value: params.get("release") },
    { key: "app", value: params.get("appVersion") },
    { key: "platform", value: params.get("platform") },
  ]);
}

export function hasActiveDeviceKpiFilters(params: URLSearchParams): boolean {
  for (const key of ["from", "to", "release", "appVersion", "platform", "deviceInstallId"]) {
    const value = params.get(key)?.trim();
    if (value) {
      return true;
    }
  }

  return false;
}

function buildDeviceKpiStateMeta(
  locale: "ko" | "en",
  query: {
    timeRange: {
      from: string;
      to: string;
    };
  },
  scopeSummary: string | undefined,
) {
  return [
    buildAdminStateTimeRangeMeta(locale, query.timeRange.from, query.timeRange.to),
    buildAdminStateScopeMeta(locale, scopeSummary),
    buildAdminStateRouteMeta(locale, "/admin/device-kpis"),
  ].filter((item): item is NonNullable<typeof item> => item !== undefined);
}

function renderMetricCard(label: string, value: string, tone?: "default" | "warn") {
  return (
    <article
      style={{
        ...cardStyle,
        padding: "18px",
        background: tone === "warn" ? "rgba(255, 247, 237, 0.8)" : "rgba(255, 255, 255, 0.82)",
      }}
    >
      <p
        style={{
          margin: 0,
          color: "#64748b",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "10px 0 0",
          fontSize: "1.45rem",
          fontWeight: 700,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </p>
    </article>
  );
}

function renderPresetLinks(
  preset: DeviceKpiScenarioPresetViewModel,
  copy: {
    failedFlows: string;
    userSummary: string;
    releaseRegression: string;
  },
): JSX.Element {
  const links = [
    preset.failedFlowsTo ? <AdminLocalizedLink key="failedFlows" to={preset.failedFlowsTo}>{copy.failedFlows}</AdminLocalizedLink> : null,
    preset.userSummaryTo ? <AdminLocalizedLink key="userSummary" to={preset.userSummaryTo}>{copy.userSummary}</AdminLocalizedLink> : null,
    preset.releaseRegressionTo ? <AdminLocalizedLink key="releaseRegression" to={preset.releaseRegressionTo}>{copy.releaseRegression}</AdminLocalizedLink> : null,
  ].filter((item): item is JSX.Element => item !== null);

  if (links.length === 0) {
    return <span>-</span>;
  }

  return (
    <span style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
      {links.map((item) => item)}
    </span>
  );
}

function isResolvedDeviceKpiState(
  state: ReturnType<typeof useDeviceKpiScreenModel>,
): state is Extract<ReturnType<typeof useDeviceKpiScreenModel>, { status: "ready" | "partial" | "stale" }> {
  return state.status === "ready" || state.status === "partial" || state.status === "stale";
}

export function DeviceKpiScreen(props: DeviceKpiScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(undefined);
  const [selectedScenarioPresetId, setSelectedScenarioPresetId] = useState<
    DeviceKpiScenarioPresetId | undefined
  >(undefined);
  const [manualRefreshSignal, setManualRefreshSignal] = useState(0);
  const refreshTick = useAdminAutoRefreshTick(props.dependencies.runtimeConfig);
  const effectiveSearchParams = useMemo(
    () => clearHiddenAdminListCursorSearchParams(searchParams),
    [searchParams],
  );
  const searchParamsKey = effectiveSearchParams.toString();
  const defaultTimeRangeRefreshSeed = resolveAdminLiveTimeRangeRefreshSeed(effectiveSearchParams, refreshTick);
  const explicitUpperBound = resolveExplicitAdminTimeRangeUpperBound(effectiveSearchParams);
  const defaultTimeRange = useMemo(
    () => createDefaultAdminListTimeRange(),
    [defaultTimeRangeRefreshSeed],
  );
  const query = useMemo(
    () => parseDeviceKpiQueryParams(effectiveSearchParams, defaultTimeRange),
    [defaultTimeRange, effectiveSearchParams, searchParamsKey],
  );
  const hasActiveFilters = useMemo(
    () => hasActiveDeviceKpiFilters(effectiveSearchParams),
    [effectiveSearchParams, searchParamsKey],
  );
  const scopeSummary = formatScopeSummary(effectiveSearchParams, locale);

  useEffect(() => {
    if (effectiveSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(withAdminDashboardLocaleSearchParams(effectiveSearchParams, locale), {
      replace: true,
    });
  }, [effectiveSearchParams, locale, searchParams, setSearchParams]);
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
      query,
    }),
    [props.dependencies, locale, query],
  );
  const state = useDeviceKpiScreenModel(screenModelDependencies, manualRefreshSignal);
  const baselineState = useDeviceKpiScreenModel(
    useMemo(
      () => ({
        ...props.dependencies,
        locale,
        skip: !hasActiveFilters,
      }),
      [props.dependencies, locale, hasActiveFilters],
    ),
    manualRefreshSignal,
  );
  const refreshCurrentScreen = useCallback(() => {
    setManualRefreshSignal((current) => current + 1);
  }, []);
  const manualRefreshRegistration = useMemo(
    () => ({
      onRefresh: refreshCurrentScreen,
      refreshing: state.status === "loading" || baselineState.status === "loading",
    }),
    [baselineState.status, refreshCurrentScreen, state.status],
  );
  useAdminManualRefreshRegistration(manualRefreshRegistration);
  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Device KPIs", ko: "실기기 지표" }),
    title: pickAdminDashboardLocaleText(locale, { en: "Device KPI Explorer", ko: "실기기 설치본 지표 탐색기" }),
    defaultScopeTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default scope: full-history latest install buckets",
      ko: "기본 범위: 전체 히스토리 최신 설치본",
    }),
    defaultScopeBody: pickAdminDashboardLocaleText(locale, {
      en: "This screen opens with the latest 100 install buckets across full history. Use the filters below only when you need to narrow by release, app version, platform, or device.",
      ko: "이 화면은 전체 히스토리 기준 최신 설치본 100건을 먼저 엽니다. 릴리스, 앱 버전, 플랫폼, 기기 기준으로 좁혀야 할 때만 아래 필터를 사용합니다.",
    }),
    authMode: pickAdminDashboardLocaleText(locale, { en: "Auth mode", ko: "인증 모드" }),
    from: pickAdminDashboardLocaleText(locale, { en: "From", ko: "시작" }),
    to: pickAdminDashboardLocaleText(locale, { en: "To", ko: "종료" }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    platform: pickAdminDashboardLocaleText(locale, { en: "Platform", ko: "플랫폼" }),
    device: pickAdminDashboardLocaleText(locale, { en: "Device", ko: "기기" }),
    apply: pickAdminDashboardLocaleText(locale, { en: "Apply filters", ko: "필터 적용" }),
    reset: pickAdminDashboardLocaleText(locale, { en: "Reset", ko: "초기화" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading device KPI data...", ko: "실기기 지표 데이터를 불러오는 중입니다..." }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "Device KPI denied", ko: "실기기 지표 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, { en: "Device KPI read permission is missing.", ko: "실기기 지표 조회 권한이 없습니다." }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "Device KPI failed", ko: "실기기 지표 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No device KPI rows", ko: "실기기 지표 행 없음" }),
    emptyBodyNoScope: pickAdminDashboardLocaleText(locale, { en: "No device KPI rows were found across the default full-history scope.", ko: "기본 전체 히스토리 범위에서 실기기 지표 행이 없습니다." }),
    emptyBodyWithScopePrefix: pickAdminDashboardLocaleText(locale, { en: "No device KPI rows match the current filters. filtered scope:", ko: "현재 필터로 좁힌 범위에 맞는 실기기 지표 행이 없습니다. 필터 범위:" }),
    eyebrow: pickAdminDashboardLocaleText(locale, { en: "Device KPI Explorer", ko: "실기기 지표 탐색기" }),
    description: pickAdminDashboardLocaleText(locale, {
      en: "The first load shows the latest 100 install buckets across full history. Add release, appVersion, platform, or device filters only when you need to narrow the scope.",
      ko: "첫 진입에서는 전체 히스토리 기준 최신 설치본 100건을 먼저 보여줍니다. 릴리스, 앱 버전, 플랫폼, 기기 필터는 범위를 좁힐 때만 추가합니다.",
    }),
    staleObservationTitle: pickAdminDashboardLocaleText(locale, {
      en: "Latest observation is older than the current query snapshot.",
      ko: "최신 관측 시각이 현재 조회 스냅샷보다 오래됐습니다.",
    }),
    staleObservationBody: pickAdminDashboardLocaleText(locale, {
      en: "The query was generated recently, but device KPI observations stopped earlier. Check upstream ingestion or projection freshness before treating this screen as up to date, and use the latest source activity card below when newer events already exist.",
      ko: "조회는 방금 생성됐지만 device KPI 관측은 더 이른 시점에서 멈췄습니다. 이 화면을 최신으로 믿기 전에 upstream 적재 또는 projection freshness를 먼저 확인하고, 더 최신 이벤트가 있으면 아래 최신 source activity 카드부터 보세요.",
    }),
    freshnessMismatchTitle: pickAdminDashboardLocaleText(locale, {
      en: "Freshness metadata and KPI baseline are pointing to different realities.",
      ko: "신선도 메타데이터와 KPI 기준 관측이 서로 다른 현실을 가리킵니다.",
    }),
    freshnessMismatchBody: pickAdminDashboardLocaleText(locale, {
      en: "A newer source event is already visible, but the KPI baseline still lags behind. Treat the freshness badge as transport metadata only, and compare the KPI baseline observed time against the latest source activity before deciding this install bucket is current.",
      ko: "더 최신 source 이벤트는 이미 보이지만 KPI 기준 관측은 아직 뒤처져 있습니다. freshness badge는 전송 메타데이터로만 읽고, 이 설치본이 최신인지 판단하기 전에 KPI 기준 관측 시각과 최신 source activity를 함께 비교하세요.",
    }),
    projectionGapTitle: pickAdminDashboardLocaleText(locale, {
      en: "Recent failed flow exists, but no KPI bucket was returned",
      ko: "최근 오류 흐름은 있지만 KPI 버킷이 반환되지 않았습니다",
    }),
    projectionGapBody: pickAdminDashboardLocaleText(locale, {
      en: "The same device or release scope has a recent failed flow, but the device KPI query returned no install bucket. Treat this as a read-model or fallback lag until the bucket appears.",
      ko: "같은 기기 또는 릴리스 범위에 최근 오류 흐름이 있지만 device KPI 조회는 설치본 버킷을 돌려주지 못했습니다. 버킷이 다시 보일 때까지 read-model 또는 fallback 지연으로 판단하세요.",
    }),
    projectionGapRowsTitle: pickAdminDashboardLocaleText(locale, {
      en: "Recent source activity newer than current KPI observation",
      ko: "현재 KPI 관측보다 더 최근인 source activity",
    }),
    projectionGapRowsBody: pickAdminDashboardLocaleText(locale, {
      en: "These rows prove newer source activity exists even though the KPI baseline has not caught up yet. Use them as the next triage anchor instead of trusting table sort order alone.",
      ko: "아래 행은 KPI baseline보다 더 최근 source activity가 이미 존재함을 보여줍니다. 표 정렬만 믿지 말고 이 행들을 다음 조사 anchor로 사용하세요.",
    }),
    latestFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Latest failed flow", ko: "최근 오류 흐름" }),
    failedFlowRequest: pickAdminDashboardLocaleText(locale, { en: "Failed request", ko: "오류 요청" }),
    failedFlowError: pickAdminDashboardLocaleText(locale, { en: "Failed error", ko: "오류 코드" }),
    inspectFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Inspect failed flow", ko: "오류 흐름 보기" }),
    scope: pickAdminDashboardLocaleText(locale, { en: "scope", ko: "범위" }),
    querySnapshot: pickAdminDashboardLocaleText(locale, { en: "Query snapshot", ko: "조회 스냅샷" }),
    metadata: pickAdminDashboardLocaleText(locale, { en: "Metadata", ko: "메타데이터" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    rows: pickAdminDashboardLocaleText(locale, { en: "Rows", ko: "행 수" }),
    sessions: pickAdminDashboardLocaleText(locale, { en: "Sessions", ko: "세션" }),
    failures: pickAdminDashboardLocaleText(locale, { en: "Failures", ko: "실패" }),
    latestObserved: pickAdminDashboardLocaleText(locale, { en: "Latest observed", ko: "최신 관측 시각" }),
    latestSourceActivity: pickAdminDashboardLocaleText(locale, { en: "Latest source activity", ko: "최신 source activity" }),
    baselineObserved: pickAdminDashboardLocaleText(locale, { en: "KPI baseline observed", ko: "KPI 기준 관측 시각" }),
    observationLag: pickAdminDashboardLocaleText(locale, { en: "Baseline lag", ko: "기준 지연 시간" }),
    freshnessAlignment: pickAdminDashboardLocaleText(locale, { en: "Freshness alignment", ko: "신선도 정합성" }),
    freshnessMismatch: pickAdminDashboardLocaleText(locale, { en: "Mismatch", ko: "불일치" }),
    freshnessAligned: pickAdminDashboardLocaleText(locale, { en: "Aligned", ko: "정상" }),
    projectionGapRowsCount: pickAdminDashboardLocaleText(locale, { en: "Source-only rows", ko: "source-only 행 수" }),
    kpiRows: pickAdminDashboardLocaleText(locale, { en: "KPI rows", ko: "지표 행" }),
    distinctUsers: pickAdminDashboardLocaleText(locale, { en: "Distinct users", ko: "고유 사용자" }),
    sessionStarts: pickAdminDashboardLocaleText(locale, { en: "Session starts", ko: "세션 시작" }),
    installEvidence: pickAdminDashboardLocaleText(locale, { en: "Install evidence", ko: "설치본 근거" }),
    installEvidenceTitle: pickAdminDashboardLocaleText(locale, {
      en: "Read the install bucket before selecting a scenario preset",
      ko: "시나리오 프리셋을 고르기 전에 설치본 버킷부터 읽습니다",
    }),
    installEvidenceBody: pickAdminDashboardLocaleText(locale, {
      en: "Start from the row that best matches the release, appVersion, platform, and failure concentration you are investigating.",
      ko: "조사 중인 릴리스, 앱 버전, 플랫폼, 실패 집중도와 가장 맞는 행에서 시작합니다.",
    }),
    scenarioPresets: pickAdminDashboardLocaleText(locale, { en: "Scenario presets", ko: "시나리오 프리셋" }),
    sameInstall: pickAdminDashboardLocaleText(locale, { en: "same install scope, faster scenario triage", ko: "같은 설치본 축에서 더 빠르게 시나리오를 조사합니다" }),
    source: pickAdminDashboardLocaleText(locale, { en: "source", ko: "소스" }),
    allOption: pickAdminDashboardLocaleText(locale, { en: "all", ko: "전체" }),
    unknown: pickAdminDashboardLocaleText(locale, { en: "Unknown", ko: "알 수 없음" }),
    lastSeen: pickAdminDashboardLocaleText(locale, { en: "Last seen", ko: "마지막 확인" }),
    auth: pickAdminDashboardLocaleText(locale, { en: "Auth", ko: "인증" }),
    pet: pickAdminDashboardLocaleText(locale, { en: "Pet", ko: "펫" }),
    today: pickAdminDashboardLocaleText(locale, { en: "Today", ko: "오늘 상태" }),
    dailyFlow: pickAdminDashboardLocaleText(locale, { en: "Daily flow", ko: "일상 흐름" }),
    healthFlow: pickAdminDashboardLocaleText(locale, { en: "Health flow", ko: "건강 흐름" }),
    receiptFlow: pickAdminDashboardLocaleText(locale, { en: "Receipt flow", ko: "영수증 흐름" }),
    topError: pickAdminDashboardLocaleText(locale, { en: "Top error", ko: "상위 오류" }),
    noRowsTitle: pickAdminDashboardLocaleText(locale, { en: "No rows", ko: "행 없음" }),
    noRowsBody: pickAdminDashboardLocaleText(locale, { en: "No device KPI rows were returned.", ko: "조회된 실기기 지표 행이 없습니다." }),
    detailTitle: pickAdminDashboardLocaleText(locale, { en: "Device KPI detail", ko: "실기기 지표 상세" }),
    install: pickAdminDashboardLocaleText(locale, { en: "Install", ko: "설치본" }),
    latestUser: pickAdminDashboardLocaleText(locale, { en: "Latest user", ko: "최근 사용자" }),
    latestUserCue: pickAdminDashboardLocaleText(locale, { en: "Latest user cue", ko: "최근 사용자 단서" }),
    latestProviderCue: pickAdminDashboardLocaleText(locale, { en: "Latest provider cue", ko: "최근 로그인 공급자 단서" }),
    latestLinkedProviders: pickAdminDashboardLocaleText(locale, { en: "Linked providers", ko: "연결된 공급자 수" }),
    latestRuntimeSummary: pickAdminDashboardLocaleText(locale, { en: "Latest runtime summary", ko: "최근 런타임 요약" }),
    latestRuntimeAppBuildNumber: pickAdminDashboardLocaleText(locale, { en: "Latest app build", ko: "최근 앱 빌드" }),
    latestRuntimeLocale: pickAdminDashboardLocaleText(locale, { en: "Latest runtime locale", ko: "최근 런타임 언어" }),
    latestRuntimeTimezone: pickAdminDashboardLocaleText(locale, { en: "Latest runtime timezone", ko: "최근 런타임 시간대" }),
    latestRuntimeTimezoneRegion: pickAdminDashboardLocaleText(locale, { en: "Latest timezone region", ko: "최근 시간대 권역" }),
    latestRuntimeDeviceModel: pickAdminDashboardLocaleText(locale, { en: "Latest device class", ko: "최근 기기 분류" }),
    latestRuntimeDeviceModelName: pickAdminDashboardLocaleText(locale, { en: "Latest device family", ko: "최근 기기 계열" }),
    latestRuntimeOsMajor: pickAdminDashboardLocaleText(locale, { en: "Latest OS bucket", ko: "최근 OS 버킷" }),
    latestRuntimeOsVersionMinor: pickAdminDashboardLocaleText(locale, { en: "Latest OS minor bucket", ko: "최근 OS 세부 버킷" }),
    latestRuntimeNetworkType: pickAdminDashboardLocaleText(locale, { en: "Latest network", ko: "최근 네트워크" }),
    latestRuntimePhysicalDevice: pickAdminDashboardLocaleText(
      locale,
      { en: "Latest device runtime", ko: "최근 기기 런타임" },
    ),
    latestRuntimeObservedAt: pickAdminDashboardLocaleText(locale, { en: "Runtime context observed at", ko: "런타임 문맥 관측 시각" }),
    scenarioCue: pickAdminDashboardLocaleText(locale, { en: "Scenario cue", ko: "시나리오 단서" }),
    headline: pickAdminDashboardLocaleText(locale, { en: "Headline", ko: "헤드라인" }),
    summary: pickAdminDashboardLocaleText(locale, { en: "Summary", ko: "요약" }),
    sources: pickAdminDashboardLocaleText(locale, { en: "Sources", ko: "소스" }),
    semantics: pickAdminDashboardLocaleText(locale, { en: "Semantics", ko: "의미" }),
    sourceStatus: pickAdminDashboardLocaleText(locale, { en: "Source status", ko: "소스 상태" }),
    latestDailyContext: pickAdminDashboardLocaleText(locale, { en: "Latest daily client context", ko: "최근 일상 클라이언트 문맥" }),
    latestPetContext: pickAdminDashboardLocaleText(locale, { en: "Latest pet request context", ko: "최근 펫 요청 문맥" }),
    latestSocialContext: pickAdminDashboardLocaleText(locale, { en: "Latest social request context", ko: "최근 소셜 요청 문맥" }),
    latestChatContext: pickAdminDashboardLocaleText(locale, { en: "Latest chat request context", ko: "최근 채팅 요청 문맥" }),
    latestReceiptContext: pickAdminDashboardLocaleText(locale, { en: "Latest receipt request context", ko: "최근 영수증 요청 문맥" }),
    latestCarePlanContext: pickAdminDashboardLocaleText(locale, { en: "Latest medication request context", ko: "최근 복약 요청 문맥" }),
    latestRideContext: pickAdminDashboardLocaleText(locale, { en: "Latest ride request context", ko: "최근 이동지원 요청 문맥" }),
    profileCompletionStep: pickAdminDashboardLocaleText(locale, { en: "Profile step", ko: "프로필 단계" }),
    attemptCountBucket: pickAdminDashboardLocaleText(locale, { en: "Attempt bucket", ko: "재시도 버킷" }),
    speciesBucket: pickAdminDashboardLocaleText(locale, { en: "Species bucket", ko: "종 버킷" }),
    breedSelectionBucket: pickAdminDashboardLocaleText(locale, { en: "Breed selection", ko: "품종 선택" }),
    petContextObservedAt: pickAdminDashboardLocaleText(locale, { en: "Pet context observed at", ko: "펫 문맥 관측 시각" }),
    targetUserLookupKind: pickAdminDashboardLocaleText(locale, { en: "Target lookup", ko: "대상 조회 경로" }),
    publicPetCountBucket: pickAdminDashboardLocaleText(locale, { en: "Public pet count", ko: "공개 펫 수" }),
    profileMutationFieldSetBucket: pickAdminDashboardLocaleText(locale, { en: "Profile mutation scope", ko: "프로필 수정 범위" }),
    socialContextObservedAt: pickAdminDashboardLocaleText(locale, { en: "Social context observed at", ko: "소셜 문맥 관측 시각" }),
    roomKind: pickAdminDashboardLocaleText(locale, { en: "Room kind", ko: "방 종류" }),
    messageLengthBucket: pickAdminDashboardLocaleText(locale, { en: "Message length", ko: "메시지 길이" }),
    membershipCountBucket: pickAdminDashboardLocaleText(locale, { en: "Membership size", ko: "참여 인원 수" }),
    chatContextObservedAt: pickAdminDashboardLocaleText(locale, { en: "Chat context observed at", ko: "채팅 문맥 관측 시각" }),
    extractSourceBucket: pickAdminDashboardLocaleText(locale, { en: "Extract source", ko: "추출 소스" }),
    imageByteSizeBucket: pickAdminDashboardLocaleText(locale, { en: "Image byte-size bucket", ko: "이미지 바이트 크기 버킷" }),
    extractionConfidenceBucket: pickAdminDashboardLocaleText(locale, { en: "AI extraction confidence", ko: "AI 추출 신뢰도" }),
    manualOverride: pickAdminDashboardLocaleText(locale, { en: "Manual override", ko: "수동 수정" }),
    categoryMutationType: pickAdminDashboardLocaleText(locale, { en: "Category mutation", ko: "카테고리 변경" }),
    receiptContextObservedAt: pickAdminDashboardLocaleText(locale, { en: "Receipt context observed at", ko: "영수증 문맥 관측 시각" }),
    scheduleRecurrenceBucket: pickAdminDashboardLocaleText(locale, { en: "Schedule recurrence", ko: "반복 일정" }),
    dosePresence: pickAdminDashboardLocaleText(locale, { en: "Dose presence", ko: "복용량 입력" }),
    occurrenceActionKind: pickAdminDashboardLocaleText(locale, { en: "Occurrence action", ko: "복용 액션" }),
    snoozeWindowBucket: pickAdminDashboardLocaleText(locale, { en: "Snooze window", ko: "미루기 시간" }),
    carePlanContextObservedAt: pickAdminDashboardLocaleText(locale, { en: "CarePlan context observed at", ko: "케어플랜 문맥 관측 시각" }),
    rideVisitLeadTimeBucket: pickAdminDashboardLocaleText(locale, { en: "Visit window", ko: "방문 시점" }),
    rideLongestAddressLengthBucket: pickAdminDashboardLocaleText(locale, { en: "Address length", ko: "주소 길이" }),
    rideMemoProvided: pickAdminDashboardLocaleText(locale, { en: "Memo provided", ko: "메모 포함" }),
    rideContextObservedAt: pickAdminDashboardLocaleText(locale, { en: "Ride context observed at", ko: "이동지원 문맥 관측 시각" }),
    speechDurationBucket: pickAdminDashboardLocaleText(locale, { en: "Speech duration bucket", ko: "음성 길이 버킷" }),
    speechDurationObservedAt: pickAdminDashboardLocaleText(locale, { en: "Speech observed at", ko: "음성 관측 시각" }),
    draftEditCountBucket: pickAdminDashboardLocaleText(locale, { en: "Draft edit bucket", ko: "초안 수정 수 버킷" }),
    draftEditObservedAt: pickAdminDashboardLocaleText(locale, { en: "Draft observed at", ko: "초안 관측 시각" }),
    discardReasonCategory: pickAdminDashboardLocaleText(locale, { en: "Discard reason", ko: "폐기 사유" }),
    discardObservedAt: pickAdminDashboardLocaleText(locale, { en: "Discard observed at", ko: "폐기 관측 시각" }),
    nextStep: pickAdminDashboardLocaleText(locale, { en: "Next step", ko: "다음 조사" }),
    allCounters: pickAdminDashboardLocaleText(locale, { en: "All counters", ko: "전체 카운터" }),
    authSuccess: pickAdminDashboardLocaleText(locale, { en: "Auth success", ko: "인증 성공" }),
    petTotal: pickAdminDashboardLocaleText(locale, { en: "Pet total", ko: "펫 합계" }),
    petCreate: pickAdminDashboardLocaleText(locale, { en: "Pet create", ko: "펫 등록" }),
    petUpdate: pickAdminDashboardLocaleText(locale, { en: "Pet update", ko: "펫 수정" }),
    petDelete: pickAdminDashboardLocaleText(locale, { en: "Pet delete", ko: "펫 삭제" }),
    petPhoto: pickAdminDashboardLocaleText(locale, { en: "Pet photo confirm", ko: "펫 사진 확정" }),
    todayStatus: pickAdminDashboardLocaleText(locale, { en: "Today status", ko: "오늘 상태" }),
    dailyTotal: pickAdminDashboardLocaleText(locale, { en: "Daily flow total", ko: "일상 흐름 합계" }),
    speechStart: pickAdminDashboardLocaleText(locale, { en: "Speech start", ko: "음성 시작" }),
    speechCancel: pickAdminDashboardLocaleText(locale, { en: "Speech cancel", ko: "음성 취소" }),
    dailyDraftCreate: pickAdminDashboardLocaleText(locale, { en: "Daily draft create", ko: "일상 초안 생성" }),
    dailyDraftUpdate: pickAdminDashboardLocaleText(locale, { en: "Daily draft update", ko: "일상 초안 수정" }),
    dailyDraftDiscard: pickAdminDashboardLocaleText(locale, { en: "Daily draft discard", ko: "일상 초안 폐기" }),
    dailySave: pickAdminDashboardLocaleText(locale, { en: "Daily save", ko: "일상 저장" }),
    dailyMedia: pickAdminDashboardLocaleText(locale, { en: "Daily media update", ko: "일상 미디어 업데이트" }),
    healthTotal: pickAdminDashboardLocaleText(locale, { en: "Health flow total", ko: "건강 흐름 합계" }),
    healthUpload: pickAdminDashboardLocaleText(locale, { en: "Health upload", ko: "건강 업로드" }),
    healthConfirm: pickAdminDashboardLocaleText(locale, { en: "Health confirm", ko: "건강 확정" }),
    healthAnalysis: pickAdminDashboardLocaleText(locale, { en: "Health analysis start", ko: "건강 분석 시작" }),
    healthSave: pickAdminDashboardLocaleText(locale, { en: "Health save", ko: "건강 저장" }),
    receiptExtract: pickAdminDashboardLocaleText(locale, { en: "Receipt extract", ko: "영수증 추출" }),
    receiptSave: pickAdminDashboardLocaleText(locale, { en: "Receipt save", ko: "영수증 저장" }),
    receiptCategoryCreate: pickAdminDashboardLocaleText(locale, { en: "Receipt category create", ko: "영수증 카테고리 생성" }),
    receiptCategoryUpdate: pickAdminDashboardLocaleText(locale, { en: "Receipt category update", ko: "영수증 카테고리 수정" }),
    receiptCategoryDelete: pickAdminDashboardLocaleText(locale, { en: "Receipt category delete", ko: "영수증 카테고리 삭제" }),
    receiptTotal: pickAdminDashboardLocaleText(locale, { en: "Receipt flow total", ko: "영수증 흐름 합계" }),
    totalSuccess: pickAdminDashboardLocaleText(locale, { en: "Total success", ko: "전체 성공" }),
    drilldown: pickAdminDashboardLocaleText(locale, { en: "Drill-down", ko: "드릴다운" }),
    failedFlows: pickAdminDashboardLocaleText(locale, { en: "Failed flows", ko: "오류 흐름" }),
    userSummary: pickAdminDashboardLocaleText(locale, { en: "User summary", ko: "사용자 요약" }),
    releaseRegression: pickAdminDashboardLocaleText(locale, { en: "Release regression", ko: "릴리스 회귀" }),
    openFailedFlows: pickAdminDashboardLocaleText(locale, { en: "Open failed flows", ko: "오류 흐름 열기" }),
    openUserSummary: pickAdminDashboardLocaleText(locale, { en: "Open user summary", ko: "사용자 요약 열기" }),
    openRegression: pickAdminDashboardLocaleText(locale, { en: "Open regression", ko: "회귀 화면 열기" }),
    freshness: pickAdminDashboardLocaleText(locale, { en: "Freshness", ko: "신선도" }),
    noSelectionTitle: pickAdminDashboardLocaleText(locale, { en: "No device KPI selected", ko: "선택된 실기기 지표 없음" }),
    noSelectionBody: pickAdminDashboardLocaleText(locale, { en: "Select a table row to open failed-flow, user summary, and release regression drill-down.", ko: "표 행을 선택하면 오류 흐름, 사용자 요약, 릴리스 회귀 드릴다운을 볼 수 있습니다." }),
    baselineTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default full-history install buckets",
      ko: "기본 전체 히스토리 설치본 목록",
    }),
    baselineBody: pickAdminDashboardLocaleText(locale, {
      en: "The current filters returned no rows, so the latest 100 install buckets across full history remain visible below for the next filter choice.",
      ko: "현재 필터 결과가 비어 있어도 다음 필터 기준을 고를 수 있도록 전체 히스토리 기준 최신 설치본 100건을 아래에 계속 보여줍니다.",
    }),
    baselineBodyActive: pickAdminDashboardLocaleText(locale, {
      en: "Even after filters are applied, keep the latest 100 install buckets across full history visible below so you can compare the narrowed result against the baseline and choose the next filter.",
      ko: "필터를 적용한 뒤에도 전체 히스토리 기준 최신 설치본 100건을 아래에 계속 보여줘, 좁힌 결과를 baseline과 바로 비교하고 다음 필터 기준을 고를 수 있게 합니다.",
    }),
  };

  useEffect(() => {
    if (state.status === "ready" || state.status === "partial" || state.status === "stale") {
      const firstRowId = state.viewModel.rows[0]?.id;

      setSelectedRowId((current) => {
        const matched = state.viewModel.rows.some((row) => row.id === current);
        return matched ? current : firstRowId;
      });
    } else {
      setSelectedRowId(undefined);
    }
  }, [state]);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSearchParams(createNextSearchParams(new FormData(event.currentTarget), locale));
  }

  function handleResetToLiveRange(): void {
    setSearchParams(withAdminDashboardLocaleSearchParams(clearExplicitAdminTimeRangeSearchParams(searchParams), locale));
  }

  const selectedRow =
    state.status === "ready" || state.status === "partial" || state.status === "stale"
      ? state.viewModel.rows.find((row) => row.id === selectedRowId)
      : undefined;
  const selectedScenarioPreset = selectedRow?.detail.scenarioPresets.find(
    (preset) => preset.id === selectedScenarioPresetId,
  );

  useEffect(() => {
    if (!selectedRow) {
      setSelectedScenarioPresetId(undefined);
      return;
    }

    setSelectedScenarioPresetId(pickDefaultDeviceKpiScenarioPresetId(selectedRow.detail));
  }, [
    selectedRow?.id,
    selectedRow?.detail.dailyFlowSuccessCountLabel,
    selectedRow?.detail.receiptFlowCountLabel,
    selectedRow?.detail.healthFlowSuccessCountLabel,
    selectedRow?.detail.petSuccessCountLabel,
    selectedRow?.detail.authSuccessCountLabel,
    selectedRow?.detail.failureCountLabel,
  ]);

  const renderBaselineContextSection = (body: string) => {
    if (!hasActiveFilters) {
      return null;
    }

    return (
      <section style={{ ...cardStyle, display: "grid", gap: "16px" }}>
        <div style={{ display: "grid", gap: "6px" }}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.baselineTitle}</p>
          <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{body}</p>
        </div>
        {baselineState.status === "loading" || baselineState.status === "idle" ? (
          <p style={{ margin: 0, color: "#475569" }}>{copy.loading}</p>
        ) : null}
        {baselineState.status === "permission_denied" ? (
          <AdminErrorState
            title={copy.deniedTitle}
            body={copy.deniedBody}
            guidance={{
              screen: "device_kpis",
              slot: "shared_error_state_guidance",
            }}
            requestId={baselineState.error.requestId}
            tone="permission_denied"
          />
        ) : null}
        {baselineState.status === "error" ? (
          <AdminErrorState
            title={copy.failedTitle}
            body={baselineState.error.message}
            guidance={{
              screen: "device_kpis",
              slot: "shared_error_state_guidance",
            }}
            requestId={baselineState.error.requestId}
            tone="error"
          />
        ) : null}
        {isResolvedDeviceKpiState(baselineState) ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "14px",
              }}
            >
              <div key="baselineRows">{renderMetricCard(copy.kpiRows, baselineState.viewModel.totalCountLabel)}</div>
              <div key="baselineUsers">{renderMetricCard(copy.distinctUsers, baselineState.viewModel.distinctUserCountLabel)}</div>
              <div key="baselineSessions">{renderMetricCard(copy.sessionStarts, baselineState.viewModel.totalSessionCountLabel)}</div>
              <div key="baselineFailures">{renderMetricCard(copy.failures, baselineState.viewModel.totalFailureCountLabel, "warn")}</div>
            </div>
            <AdminDataTable
              columns={[
                {
                  id: "lastSeenAt",
                  label: copy.lastSeen,
                  sortValue: (row) => row.lastSeenAt,
                  defaultSortDirection: "desc",
                  renderCell: (row) => row.lastSeenAtLabel,
                  width: "148px",
                },
                {
                  id: "deviceInstallId",
                  label: copy.device,
                  sortValue: (row) => row.deviceInstallId,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <span>{row.deviceInstallIdLabel}</span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{row.latestRuntimeSummaryLabel}</span>
                    </div>
                  ),
                },
                {
                  id: "platform",
                  label: copy.platform,
                  sortValue: (row) => row.platform ?? row.platformLabel,
                  renderCell: (row) => row.platformLabel,
                },
                {
                  id: "release",
                  label: copy.release,
                  renderCell: (row) => row.releaseLabel,
                },
                {
                  id: "appVersion",
                  label: copy.app,
                  renderCell: (row) => row.appVersionLabel,
                },
                {
                  id: "users",
                  label: copy.distinctUsers,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <span>
                        {formatAdminHashedAccountCueLabel(
                          row.latestUserIdHashLabel,
                          row.latestUserAccountIdentityLabel,
                        )}
                      </span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem" }}>
                        {row.latestUserProviderIdentityLabel}
                      </span>
                      <span style={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                        {row.latestUserLinkedProviderCountLabel}
                      </span>
                    </div>
                  ),
                },
                {
                  id: "failures",
                  label: copy.failures,
                  align: "right",
                  renderCell: (row) => row.failureCountLabel,
                },
                {
                  id: "topError",
                  label: copy.topError,
                  renderCell: (row) => row.topErrorCodeLabel,
                },
              ]}
              initialSortState={{
                columnId: "lastSeenAt",
                direction: "desc",
              }}
              getRowId={(row) => row.id}
              rows={baselineState.viewModel.rows}
            />
          </>
        ) : null}
      </section>
    );
  };

  const renderFilterForm = () => (
    <form onSubmit={handleFilterSubmit}>
      <AdminFilterBar
        items={[
          {
            id: "from",
            label: copy.from,
            control: (
              <input defaultValue={searchParams.get("from") ?? ""} id="from" name="from" style={baseControlStyle()} />
            ),
          },
          {
            id: "to",
            label: copy.to,
            control: (
              <input defaultValue={searchParams.get("to") ?? ""} id="to" name="to" style={baseControlStyle()} />
            ),
          },
          {
            id: "release",
            label: copy.release,
            control: (
              <input
                defaultValue={searchParams.get("release") ?? ""}
                id="release"
                name="release"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "appVersion",
            label: copy.app,
            control: (
              <input
                defaultValue={searchParams.get("appVersion") ?? ""}
                id="appVersion"
                name="appVersion"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "platform",
            label: copy.platform,
            control: (
              <select
                defaultValue={searchParams.get("platform") ?? ""}
                id="platform"
                name="platform"
                style={baseControlStyle()}
              >
                <option value="">{copy.allOption}</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
                <option value="web">Web</option>
                <option value="api">API</option>
                <option value="unknown">{copy.unknown}</option>
              </select>
            ),
          },
          {
            id: "deviceInstallId",
            label: copy.device,
            control: (
              <input
                defaultValue={searchParams.get("deviceInstallId") ?? ""}
                id="deviceInstallId"
                name="deviceInstallId"
                style={baseControlStyle()}
              />
            ),
          },
        ]}
        actions={
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              style={{
                minHeight: "40px",
                padding: "0 14px",
                borderRadius: "999px",
                border: "none",
                background: "#0f766e",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
              type="submit"
            >
              {copy.apply}
            </button>
            <AdminLocalizedLink
              to="/admin/device-kpis"
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "40px",
                padding: "0 14px",
                borderRadius: "999px",
                border: "1px solid rgba(148, 163, 184, 0.45)",
                color: "#18212b",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              {copy.reset}
            </AdminLocalizedLink>
          </div>
        }
      />
    </form>
  );

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              {copy.title}
            </h3>
          </div>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {copy.authMode}: {formatAdminAuthModeLabel(props.dependencies.runtimeConfig.authMode, locale)}
          </p>
        </div>
      </header>

      {explicitUpperBound ? (
        <AdminLiveRangeBanner
          explicitUpperBound={explicitUpperBound}
          locale={locale}
          onResetToLive={handleResetToLiveRange}
        />
      ) : null}

      <section style={{ ...cardStyle, display: "grid", gap: "8px" }}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.defaultScopeTitle}</p>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{copy.defaultScopeBody}</p>
      </section>

      {state.status === "loading" || state.status === "idle" ? (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      ) : null}

      {state.status === "permission_denied" ? (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{
            screen: "device_kpis",
            slot: "shared_error_state_guidance",
          }}
          meta={buildDeviceKpiStateMeta(locale, query, scopeSummary)}
          requestId={state.error.requestId}
          tone="permission_denied"
        />
      ) : null}

      {state.status === "error" ? (
        <AdminErrorState
          title={copy.failedTitle}
          body={state.error.message}
          guidance={{
            screen: "device_kpis",
            slot: "shared_error_state_guidance",
          }}
          meta={buildDeviceKpiStateMeta(locale, query, scopeSummary)}
          requestId={state.error.requestId}
          tone="error"
        />
      ) : null}

      {state.status === "empty" ? (
        <>
          <AdminEmptyState
            title={copy.emptyTitle}
            body={
              scopeSummary
                ? `${copy.emptyBodyWithScopePrefix} ${scopeSummary}`
                : copy.emptyBodyNoScope
            }
            guidance={{
              screen: "device_kpis",
              slot: "shared_empty_state_guidance",
            }}
            meta={buildDeviceKpiStateMeta(locale, query, scopeSummary)}
          />
          {renderBaselineContextSection(copy.baselineBody)}
        </>
      ) : null}

      {state.status === "ready" || state.status === "partial" || state.status === "stale" ? (
        <>
          <section
            style={{
              ...cardStyle,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 0.85fr)",
              gap: "18px",
            }}
          >
            <div>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", fontWeight: 700 }}>
                {copy.eyebrow}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1.35rem", fontWeight: 700 }}>
                {state.viewModel.selectedScopeLabel}
              </p>
              <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>
                {copy.description}
              </p>
              <AdminOperatorExplanationBlock
                screen="device_kpis"
                slot="device_kpis_top_summary"
                style={{ marginTop: "14px" }}
              />
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "14px" }}>
                <AdminStatusBadge value={state.status} />
                {scopeSummary ? (
                  <span style={{ color: "#64748b", fontSize: "0.88rem" }}>{copy.scope}: {scopeSummary}</span>
                ) : null}
              </div>
              {state.viewModel.latestObservedLagging ? (
                <div
                  style={{
                    marginTop: "14px",
                    borderRadius: "16px",
                    border: "1px solid rgba(245, 158, 11, 0.28)",
                    background: "rgba(255, 247, 237, 0.95)",
                    padding: "14px 16px",
                  }}
                >
                  <p style={{ margin: 0, color: "#9a3412", fontSize: "0.88rem", fontWeight: 700 }}>
                    {copy.staleObservationTitle}
                  </p>
                  <p style={{ margin: "8px 0 0", color: "#7c2d12", lineHeight: 1.6, fontSize: "0.92rem" }}>
                    {copy.staleObservationBody}
                  </p>
                </div>
              ) : null}
              {state.viewModel.freshnessAlignment === "mismatch" ? (
                <div
                  style={{
                    marginTop: "14px",
                    borderRadius: "16px",
                    border: "1px solid rgba(249, 115, 22, 0.28)",
                    background: "rgba(255, 247, 237, 0.95)",
                    padding: "14px 16px",
                  }}
                >
                  <p style={{ margin: 0, color: "#9a3412", fontSize: "0.88rem", fontWeight: 700 }}>
                    {copy.freshnessMismatchTitle}
                  </p>
                  <p style={{ margin: "8px 0 0", color: "#7c2d12", lineHeight: 1.6, fontSize: "0.92rem" }}>
                    {copy.freshnessMismatchBody}
                  </p>
                </div>
              ) : null}
            </div>

            <AdminDetailPanel
              title={copy.querySnapshot}
              sections={[
                {
                  title: copy.metadata,
                  items: [
                    { key: "generatedAt", label: copy.generated, value: state.viewModel.generatedAtLabel },
                    { key: "requestId", label: copy.request, value: state.viewModel.requestId },
                    { key: "baselineObservedAt", label: copy.baselineObserved, value: state.viewModel.baselineObservedAtLabel },
                    { key: "latestObservedAt", label: copy.latestObserved, value: state.viewModel.latestObservedAtLabel },
                    { key: "latestSourceActivityAt", label: copy.latestSourceActivity, value: state.viewModel.latestSourceActivityAtLabel },
                    { key: "observationLag", label: copy.observationLag, value: state.viewModel.observationLagLabel },
                    {
                      key: "freshnessAlignment",
                      label: copy.freshnessAlignment,
                      value: (
                        <AdminStatusBadge
                          value={state.viewModel.freshnessAlignment === "mismatch" ? "warning" : "ready"}
                          label={
                            state.viewModel.freshnessAlignment === "mismatch"
                              ? copy.freshnessMismatch
                              : copy.freshnessAligned
                          }
                        />
                      ),
                    },
                    { key: "projectionGapRowCount", label: copy.projectionGapRowsCount, value: state.viewModel.projectionGapRowCountLabel },
                    { key: "totalRows", label: copy.rows, value: state.viewModel.totalCountLabel },
                    { key: "totalSessions", label: copy.sessions, value: state.viewModel.totalSessionCountLabel },
                    { key: "totalFailures", label: copy.failures, value: state.viewModel.totalFailureCountLabel },
                  ],
                },
              ]}
            />
          </section>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "14px",
            }}
          >
            <div key="rows">{renderMetricCard(copy.kpiRows, state.viewModel.totalCountLabel)}</div>
            <div key="users">{renderMetricCard(copy.distinctUsers, state.viewModel.distinctUserCountLabel)}</div>
            <div key="sessions">{renderMetricCard(copy.sessionStarts, state.viewModel.totalSessionCountLabel)}</div>
            <div key="failures">{renderMetricCard(copy.failures, state.viewModel.totalFailureCountLabel, "warn")}</div>
            <div key="latestObserved">{renderMetricCard(copy.latestObserved, state.viewModel.latestObservedAtLabel)}</div>
            {state.viewModel.observationLagLabel !== "-" ? (
              <div key="observationLag">
                {renderMetricCard(copy.observationLag, state.viewModel.observationLagLabel, "warn")}
              </div>
            ) : null}
            {state.viewModel.projectionGapRows.length > 0 ? (
              <div key="latestSourceActivity">
                {renderMetricCard(copy.latestSourceActivity, state.viewModel.latestSourceActivityAtLabel, "warn")}
              </div>
            ) : null}
            {state.viewModel.projectionGapRows.length > 0 ? (
              <div key="projectionGapRowCount">
                {renderMetricCard(copy.projectionGapRowsCount, state.viewModel.projectionGapRowCountLabel, "warn")}
              </div>
            ) : null}
          </div>

          {state.viewModel.projectionGapEvidence ? (
            <article
              style={{
                ...cardStyle,
                padding: "18px",
                background: "rgba(255, 247, 237, 0.82)",
                border: "1px solid rgba(251, 146, 60, 0.32)",
              }}
            >
              <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.projectionGapTitle}</p>
              <p style={{ margin: "10px 0 0", color: "#7c2d12", lineHeight: 1.6, maxWidth: "54rem" }}>
                {copy.projectionGapBody}
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "12px",
                  marginTop: "14px",
                }}
              >
                <div>
                  <p style={{ margin: 0, color: "#9a3412", fontSize: "0.78rem", fontWeight: 700 }}>{copy.latestFailedFlow}</p>
                  <p style={{ margin: "6px 0 0", fontWeight: 700 }}>{state.viewModel.projectionGapEvidence.latestFailedFlowAtLabel}</p>
                </div>
                <div>
                  <p style={{ margin: 0, color: "#9a3412", fontSize: "0.78rem", fontWeight: 700 }}>{copy.failedFlowRequest}</p>
                  <p style={{ margin: "6px 0 0", fontWeight: 700 }}>{state.viewModel.projectionGapEvidence.requestId ?? "-"}</p>
                </div>
                <div>
                  <p style={{ margin: 0, color: "#9a3412", fontSize: "0.78rem", fontWeight: 700 }}>{copy.failedFlowError}</p>
                  <p style={{ margin: "6px 0 0", fontWeight: 700 }}>{state.viewModel.projectionGapEvidence.errorCodeLabel}</p>
                </div>
              </div>
            </article>
          ) : null}

          {state.viewModel.projectionGapRows.length > 0 ? (
            <section style={cardStyle}>
              <div style={{ display: "grid", gap: "6px", marginBottom: "14px" }}>
                <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.projectionGapRowsTitle}</p>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{copy.projectionGapRowsBody}</p>
              </div>
              <AdminDataTable
                columns={[
                  {
                    id: "latestFailedFlowAt",
                    label: copy.latestFailedFlow,
                    sortValue: (row) => row.latestFailedFlowAt,
                    defaultSortDirection: "desc",
                    renderCell: (row) => row.latestFailedFlowAtLabel,
                    width: "168px",
                  },
                  {
                    id: "deviceInstallId",
                    label: copy.device,
                    sortValue: (row) => row.deviceInstallIdLabel,
                    renderCell: (row) => row.deviceInstallIdLabel,
                  },
                  {
                    id: "release",
                    label: copy.release,
                    sortValue: (row) => row.releaseLabel,
                    renderCell: (row) => row.releaseLabel,
                  },
                  {
                    id: "appVersion",
                    label: copy.app,
                    sortValue: (row) => row.appVersionLabel,
                    renderCell: (row) => row.appVersionLabel,
                  },
                  {
                    id: "platform",
                    label: copy.platform,
                    sortValue: (row) => row.platformLabel,
                    renderCell: (row) => row.platformLabel,
                  },
                  {
                    id: "errorCode",
                    label: copy.failedFlowError,
                    sortValue: (row) => row.errorCodeLabel,
                    renderCell: (row) => row.errorCodeLabel,
                  },
                  {
                    id: "requestId",
                    label: copy.failedFlowRequest,
                    sortValue: (row) => row.requestId,
                    renderCell: (row) =>
                      row.failedFlowsTo ? (
                        <AdminLocalizedLink to={row.failedFlowsTo}>{copy.inspectFailedFlow}</AdminLocalizedLink>
                      ) : (
                        row.requestId ?? "-"
                      ),
                  },
                ]}
                initialSortState={{
                  columnId: "latestFailedFlowAt",
                  direction: "desc",
                }}
                getRowId={(row) => row.id}
                rows={state.viewModel.projectionGapRows}
              />
            </section>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.45fr) minmax(320px, 0.85fr)",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <div style={{ display: "grid", gap: "16px" }}>
              {selectedRow ? (
                <section style={cardStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.scenarioPresets}</p>
                      <h4 style={{ margin: "8px 0 0", fontSize: "1.1rem", letterSpacing: "-0.02em" }}>
                        {selectedRow.detail.deviceInstallIdLabel} / {selectedRow.detail.platformLabel}
                      </h4>
                    </div>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.sameInstall}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "12px",
                      marginTop: "16px",
                    }}
                  >
                    {selectedRow.detail.scenarioPresets.map((preset) => {
                      const selected = preset.id === selectedScenarioPreset?.id;
                      return (
                        <button
                          key={preset.id}
                          onClick={() => setSelectedScenarioPresetId(preset.id)}
                          style={{
                            textAlign: "left",
                            minHeight: "158px",
                            padding: "16px",
                            borderRadius: "18px",
                            border: selected
                              ? "1px solid rgba(15, 118, 110, 0.7)"
                              : "1px solid rgba(148, 163, 184, 0.3)",
                            background: selected ? "rgba(240, 253, 250, 0.95)" : "rgba(248, 250, 252, 0.82)",
                            boxShadow: selected ? "0 14px 28px rgba(15, 118, 110, 0.12)" : "none",
                            cursor: "pointer",
                          }}
                          type="button"
                        >
                          <p
                            style={{
                              margin: 0,
                              color: selected ? "#0f766e" : "#64748b",
                              fontSize: "0.76rem",
                              fontWeight: 700,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                            }}
                          >
                            {preset.title}
                          </p>
                          <p style={{ margin: "10px 0 0", fontSize: "1.1rem", fontWeight: 700 }}>
                            {preset.metricValueLabel}
                          </p>
                          <p style={{ margin: "6px 0 0", color: "#18212b", fontWeight: 700 }}>{preset.headline}</p>
                          <p style={{ margin: "8px 0 0", color: "#475569", fontSize: "0.88rem", lineHeight: 1.5 }}>
                            {preset.summary}
                          </p>
                          <p style={{ margin: "10px 0 0", color: "#64748b", fontSize: "0.82rem" }}>
                            {copy.source}: {preset.sourceSummary}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section style={{ display: "grid", gap: "8px" }}>
                <div style={{ display: "grid", gap: "6px" }}>
                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {copy.installEvidence}
                  </p>
                  <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#18212b" }}>
                    {copy.installEvidenceTitle}
                  </p>
                  <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                    {copy.installEvidenceBody}
                  </p>
                </div>
                <AdminOperatorExplanationBlock
                  screen="device_kpis"
                  slot="device_kpis_table_reading_order"
                />
              </section>

              <AdminDataTable
                columns={[
                  {
                    id: "deviceInstallId",
                    label: copy.device,
                    sortValue: (row) => row.deviceInstallId,
                    renderCell: (row) => (
                      <div style={{ display: "grid", gap: "4px" }}>
                        <span>{row.deviceInstallIdLabel}</span>
                        <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{row.latestRuntimeSummaryLabel}</span>
                      </div>
                    ),
                    width: "160px",
                  },
                  {
                    id: "platform",
                    label: copy.platform,
                    sortValue: (row) => row.platform ?? row.platformLabel,
                    renderCell: (row) => row.platformLabel,
                  },
                  {
                    id: "appVersion",
                    label: copy.app,
                    sortValue: (row) => row.appVersion ?? row.appVersionLabel,
                    renderCell: (row) => row.appVersionLabel,
                  },
                  {
                    id: "release",
                    label: copy.release,
                    sortValue: (row) => row.release ?? row.releaseLabel,
                    renderCell: (row) => row.releaseLabel,
                  },
                  {
                    id: "lastSeenAt",
                    label: copy.lastSeen,
                    sortValue: (row) => row.lastSeenAt,
                    defaultSortDirection: "desc",
                    renderCell: (row) => row.lastSeenAtLabel,
                    width: "148px",
                  },
                  {
                    id: "users",
                    label: copy.distinctUsers,
                    renderCell: (row) => (
                      <div style={{ display: "grid", gap: "4px" }}>
                        <span>
                          {formatAdminHashedAccountCueLabel(
                            row.latestUserIdHashLabel,
                            row.latestUserAccountIdentityLabel,
                          )}
                        </span>
                        <span style={{ color: "#64748b", fontSize: "0.82rem" }}>
                          {row.latestUserProviderIdentityLabel}
                        </span>
                      </div>
                    ),
                  },
                  {
                    id: "sessions",
                    label: copy.sessions,
                    sortValue: (row) => row.sessionStartCount,
                    defaultSortDirection: "desc",
                    align: "right",
                    renderCell: (row) => row.sessionStartCountLabel,
                  },
                  { id: "auth", label: copy.auth, align: "right", renderCell: (row) => row.authSuccessCountLabel },
                  { id: "pet", label: copy.pet, align: "right", renderCell: (row) => row.petSuccessCountLabel },
                  { id: "today", label: copy.today, align: "right", renderCell: (row) => row.todayStatusSaveSuccessCountLabel },
                  { id: "daily", label: copy.dailyFlow, align: "right", renderCell: (row) => row.dailyFlowSuccessCountLabel },
                  { id: "health", label: copy.healthFlow, align: "right", renderCell: (row) => row.healthFlowSuccessCountLabel },
                  { id: "receipt", label: copy.receiptFlow, align: "right", renderCell: (row) => row.receiptFlowCountLabel },
                  {
                    id: "failure",
                    label: copy.failures,
                    sortValue: (row) => row.failureCount,
                    defaultSortDirection: "desc",
                    align: "right",
                    renderCell: (row) => row.failureCountLabel,
                  },
                  { id: "topError", label: copy.topError, renderCell: (row) => row.topErrorCodeLabel },
                ]}
                emptyState={<AdminEmptyState title={copy.noRowsTitle} body={copy.noRowsBody} />}
                getRowId={(row) => row.id}
                initialSortState={{
                  columnId: "lastSeenAt",
                  direction: "desc",
                }}
                onRowClick={(row) => setSelectedRowId(row.id)}
                rows={state.viewModel.rows}
              />
            </div>

            {selectedRow ? (
              <AdminDetailPanel
                title={copy.detailTitle}
                headerAction={
                  selectedScenarioPreset ? (
                    <AdminStatusBadge value="ready" label={selectedScenarioPreset.title} />
                  ) : undefined
                }
                sections={[
                  {
                    title: copy.install,
                    items: [
                      { key: "device", label: copy.device, value: selectedRow.detail.deviceInstallId },
                      { key: "platform", label: copy.platform, value: selectedRow.detail.platformLabel },
                      { key: "appVersion", label: copy.app, value: selectedRow.detail.appVersionLabel },
                      { key: "release", label: copy.release, value: selectedRow.detail.releaseLabel },
                      { key: "lastSeenAt", label: copy.lastSeen, value: selectedRow.detail.lastSeenAtLabel },
                      { key: "latestUser", label: copy.latestUser, value: selectedRow.detail.latestUserIdHashLabel },
                      { key: "latestUserCue", label: copy.latestUserCue, value: selectedRow.detail.latestUserAccountIdentityLabel },
                      {
                        key: "latestProviderCue",
                        label: copy.latestProviderCue,
                        value: selectedRow.detail.latestUserProviderIdentityLabel,
                      },
                      {
                        key: "latestLinkedProviders",
                        label: copy.latestLinkedProviders,
                        value: selectedRow.detail.latestUserLinkedProviderCountLabel,
                      },
                      {
                        key: "latestRuntimeSummary",
                        label: copy.latestRuntimeSummary,
                        value: selectedRow.detail.latestRuntimeSummaryLabel,
                      },
                      {
                        key: "latestRuntimeAppBuildNumber",
                        label: copy.latestRuntimeAppBuildNumber,
                        value: selectedRow.detail.latestRuntimeAppBuildNumberLabel,
                      },
                      {
                        key: "latestRuntimeLocale",
                        label: copy.latestRuntimeLocale,
                        value: selectedRow.detail.latestRuntimeLocaleLabel,
                      },
                      {
                        key: "latestRuntimeTimezone",
                        label: copy.latestRuntimeTimezone,
                        value: selectedRow.detail.latestRuntimeTimezoneOffsetBucketLabel,
                      },
                      {
                        key: "latestRuntimeTimezoneRegion",
                        label: copy.latestRuntimeTimezoneRegion,
                        value: selectedRow.detail.latestRuntimeTimezoneNameBucketLabel,
                      },
                      {
                        key: "latestRuntimeDeviceModel",
                        label: copy.latestRuntimeDeviceModel,
                        value: selectedRow.detail.latestRuntimeDeviceModelBucketLabel,
                      },
                      {
                        key: "latestRuntimeDeviceModelName",
                        label: copy.latestRuntimeDeviceModelName,
                        value: selectedRow.detail.latestRuntimeDeviceModelNameBucketLabel,
                      },
                      {
                        key: "latestRuntimeOsMajor",
                        label: copy.latestRuntimeOsMajor,
                        value: selectedRow.detail.latestRuntimeOsMajorBucketLabel,
                      },
                      {
                        key: "latestRuntimeOsVersionMinor",
                        label: copy.latestRuntimeOsVersionMinor,
                        value: selectedRow.detail.latestRuntimeOsVersionMinorBucketLabel,
                      },
                      {
                        key: "latestRuntimeNetworkType",
                        label: copy.latestRuntimeNetworkType,
                        value: selectedRow.detail.latestRuntimeNetworkTypeBucketLabel,
                      },
                      {
                        key: "latestRuntimePhysicalDevice",
                        label: copy.latestRuntimePhysicalDevice,
                        value: selectedRow.detail.latestRuntimePhysicalDeviceBucketLabel,
                      },
                      {
                        key: "latestRuntimeObservedAt",
                        label: copy.latestRuntimeObservedAt,
                        value: selectedRow.detail.latestRuntimeContextAtLabel,
                      },
                    ],
                  },
                  ...(selectedScenarioPreset
                    ? [
                        {
                          title: copy.scenarioCue,
                          items: [
                            { key: "headline", label: copy.headline, value: selectedScenarioPreset.headline },
                            { key: "metric", label: selectedScenarioPreset.metricLabel, value: selectedScenarioPreset.metricValueLabel },
                            { key: "summary", label: copy.summary, value: selectedScenarioPreset.summary },
                            { key: "sources", label: copy.sources, value: selectedScenarioPreset.sourceSummary },
                            {
                              key: "semantics",
                              label: copy.semantics,
                              value: (
                                <div style={{ display: "grid", gap: "8px" }}>
                                  <span>{selectedScenarioPreset.semantics}</span>
                                  <AdminOperatorExplanationBlock
                                    screen="device_kpis"
                                    slot="device_kpis_preset_semantics"
                                  />
                                </div>
                              ),
                            },
                            {
                              key: "ctas",
                              label: copy.nextStep,
                              value: renderPresetLinks(selectedScenarioPreset, {
                                failedFlows: copy.failedFlows,
                                userSummary: copy.userSummary,
                                releaseRegression: copy.releaseRegression,
                              }),
                            },
                          ],
                        },
                        ...(selectedScenarioPreset.id === "daily" && selectedRow.detail.dailyContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestDailyContext,
                                items: [
                                  {
                                    key: "speechDurationBucket",
                                    label: copy.speechDurationBucket,
                                    value: selectedRow.detail.dailyContextCue.speechDurationBucketLabel,
                                  },
                                  {
                                    key: "speechDurationObservedAt",
                                    label: copy.speechDurationObservedAt,
                                    value: selectedRow.detail.dailyContextCue.speechDurationBucketObservedAtLabel,
                                  },
                                  {
                                    key: "draftEditCountBucket",
                                    label: copy.draftEditCountBucket,
                                    value: selectedRow.detail.dailyContextCue.draftEditCountBucketLabel,
                                  },
                                  {
                                    key: "draftEditObservedAt",
                                    label: copy.draftEditObservedAt,
                                    value: selectedRow.detail.dailyContextCue.draftEditCountBucketObservedAtLabel,
                                  },
                                  {
                                    key: "discardReasonCategory",
                                    label: copy.discardReasonCategory,
                                    value: selectedRow.detail.dailyContextCue.draftDiscardReasonCategoryLabel,
                                  },
                                  {
                                    key: "discardObservedAt",
                                    label: copy.discardObservedAt,
                                    value: selectedRow.detail.dailyContextCue.draftDiscardReasonCategoryObservedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(selectedScenarioPreset.id === "pet" && selectedRow.detail.petContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestPetContext,
                                items: [
                                  {
                                    key: "profileCompletionStep",
                                    label: copy.profileCompletionStep,
                                    value: selectedRow.detail.petContextCue.profileCompletionStepLabel,
                                  },
                                  {
                                    key: "attemptCountBucket",
                                    label: copy.attemptCountBucket,
                                    value: selectedRow.detail.petContextCue.attemptCountBucketLabel,
                                  },
                                  {
                                    key: "speciesBucket",
                                    label: copy.speciesBucket,
                                    value: selectedRow.detail.petContextCue.speciesBucketLabel,
                                  },
                                  {
                                    key: "breedSelectionBucket",
                                    label: copy.breedSelectionBucket,
                                    value: selectedRow.detail.petContextCue.breedSelectionBucketLabel,
                                  },
                                  {
                                    key: "petContextObservedAt",
                                    label: copy.petContextObservedAt,
                                    value: selectedRow.detail.petContextCue.observedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(selectedScenarioPreset.id === "social" && selectedRow.detail.socialContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestSocialContext,
                                items: [
                                  {
                                    key: "targetUserLookupKind",
                                    label: copy.targetUserLookupKind,
                                    value: selectedRow.detail.socialContextCue.targetUserLookupKindLabel,
                                  },
                                  {
                                    key: "publicPetCountBucket",
                                    label: copy.publicPetCountBucket,
                                    value: selectedRow.detail.socialContextCue.publicPetCountBucketLabel,
                                  },
                                  {
                                    key: "profileMutationFieldSetBucket",
                                    label: copy.profileMutationFieldSetBucket,
                                    value: selectedRow.detail.socialContextCue.profileMutationFieldSetBucketLabel,
                                  },
                                  {
                                    key: "socialContextObservedAt",
                                    label: copy.socialContextObservedAt,
                                    value: selectedRow.detail.socialContextCue.observedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(selectedScenarioPreset.id === "chat" && selectedRow.detail.chatContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestChatContext,
                                items: [
                                  {
                                    key: "roomKind",
                                    label: copy.roomKind,
                                    value: selectedRow.detail.chatContextCue.roomKindLabel,
                                  },
                                  {
                                    key: "messageLengthBucket",
                                    label: copy.messageLengthBucket,
                                    value: selectedRow.detail.chatContextCue.messageLengthBucketLabel,
                                  },
                                  {
                                    key: "membershipCountBucket",
                                    label: copy.membershipCountBucket,
                                    value: selectedRow.detail.chatContextCue.membershipCountBucketLabel,
                                  },
                                  {
                                    key: "chatContextObservedAt",
                                    label: copy.chatContextObservedAt,
                                    value: selectedRow.detail.chatContextCue.observedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(selectedScenarioPreset.id === "receipt" && selectedRow.detail.receiptContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestReceiptContext,
                                items: [
                                  {
                                    key: "extractSourceBucket",
                                    label: copy.extractSourceBucket,
                                    value: selectedRow.detail.receiptContextCue.extractSourceBucketLabel,
                                  },
                                  {
                                    key: "imageByteSizeBucket",
                                    label: copy.imageByteSizeBucket,
                                    value: selectedRow.detail.receiptContextCue.imageByteSizeBucketLabel,
                                  },
                                  {
                                    key: "extractionConfidenceBucket",
                                    label: copy.extractionConfidenceBucket,
                                    value: selectedRow.detail.receiptContextCue.extractionConfidenceBucketLabel,
                                  },
                                  {
                                    key: "manualOverride",
                                    label: copy.manualOverride,
                                    value: selectedRow.detail.receiptContextCue.manualOverrideLabel,
                                  },
                                  {
                                    key: "categoryMutationType",
                                    label: copy.categoryMutationType,
                                    value: selectedRow.detail.receiptContextCue.categoryMutationTypeLabel,
                                  },
                                  {
                                    key: "receiptContextObservedAt",
                                    label: copy.receiptContextObservedAt,
                                    value: selectedRow.detail.receiptContextCue.observedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(selectedScenarioPreset.id === "medication" &&
                        selectedRow.detail.carePlanContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestCarePlanContext,
                                items: [
                                  {
                                    key: "scheduleRecurrenceBucket",
                                    label: copy.scheduleRecurrenceBucket,
                                    value:
                                      selectedRow.detail.carePlanContextCue
                                        .scheduleRecurrenceBucketLabel,
                                  },
                                  {
                                    key: "dosePresence",
                                    label: copy.dosePresence,
                                    value: selectedRow.detail.carePlanContextCue.dosePresenceLabel,
                                  },
                                  {
                                    key: "occurrenceActionKind",
                                    label: copy.occurrenceActionKind,
                                    value:
                                      selectedRow.detail.carePlanContextCue
                                        .occurrenceActionKindLabel,
                                  },
                                  {
                                    key: "snoozeWindowBucket",
                                    label: copy.snoozeWindowBucket,
                                    value:
                                      selectedRow.detail.carePlanContextCue
                                        .snoozeWindowBucketLabel,
                                  },
                                  {
                                    key: "carePlanContextObservedAt",
                                    label: copy.carePlanContextObservedAt,
                                    value: selectedRow.detail.carePlanContextCue.observedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(selectedScenarioPreset.id === "ride" && selectedRow.detail.rideContextCue.hasAnyContext
                          ? [
                              {
                                title: copy.latestRideContext,
                                items: [
                                  {
                                    key: "rideVisitLeadTimeBucket",
                                    label: copy.rideVisitLeadTimeBucket,
                                    value: selectedRow.detail.rideContextCue.visitLeadTimeBucketLabel,
                                  },
                                  {
                                    key: "rideLongestAddressLengthBucket",
                                    label: copy.rideLongestAddressLengthBucket,
                                    value: selectedRow.detail.rideContextCue.longestAddressLengthBucketLabel,
                                  },
                                  {
                                    key: "rideMemoProvided",
                                    label: copy.rideMemoProvided,
                                    value: selectedRow.detail.rideContextCue.memoProvidedLabel,
                                  },
                                  {
                                    key: "rideContextObservedAt",
                                    label: copy.rideContextObservedAt,
                                    value: selectedRow.detail.rideContextCue.observedAtLabel,
                                  },
                                ],
                              },
                            ]
                          : []),
                      ]
                    : []),
                  {
                    title: copy.allCounters,
                    items: [
                      { key: "sessions", label: copy.sessions, value: selectedRow.detail.sessionStartCountLabel },
                      { key: "auth", label: copy.authSuccess, value: selectedRow.detail.authSuccessCountLabel },
                      { key: "petTotal", label: copy.petTotal, value: selectedRow.detail.petSuccessCountLabel },
                      { key: "petCreate", label: copy.petCreate, value: selectedRow.detail.petCreateSuccessCountLabel },
                      { key: "petUpdate", label: copy.petUpdate, value: selectedRow.detail.petUpdateSuccessCountLabel },
                      { key: "petDelete", label: copy.petDelete, value: selectedRow.detail.petDeleteSuccessCountLabel },
                      { key: "petPhoto", label: copy.petPhoto, value: selectedRow.detail.petPhotoConfirmSuccessCountLabel },
                      { key: "today", label: copy.todayStatus, value: selectedRow.detail.todayStatusSaveSuccessCountLabel },
                      { key: "dailyTotal", label: copy.dailyTotal, value: selectedRow.detail.dailyFlowSuccessCountLabel },
                      { key: "dailySpeechStart", label: copy.speechStart, value: selectedRow.detail.dailySpeechCaptureStartCountLabel },
                      { key: "dailySpeechCancel", label: copy.speechCancel, value: selectedRow.detail.dailySpeechCaptureCancelCountLabel },
                      { key: "dailyDraft", label: copy.dailyDraftCreate, value: selectedRow.detail.dailyDraftCreateSuccessCountLabel },
                      { key: "dailyDraftUpdate", label: copy.dailyDraftUpdate, value: selectedRow.detail.dailyDraftUpdateCountLabel },
                      { key: "dailyDraftDiscard", label: copy.dailyDraftDiscard, value: selectedRow.detail.dailyDraftDiscardCountLabel },
                      { key: "dailySave", label: copy.dailySave, value: selectedRow.detail.dailyRecordSaveSuccessCountLabel },
                      { key: "dailyMedia", label: copy.dailyMedia, value: selectedRow.detail.dailyMediaUpdateSuccessCountLabel },
                      { key: "healthTotal", label: copy.healthTotal, value: selectedRow.detail.healthFlowSuccessCountLabel },
                      { key: "healthUpload", label: copy.healthUpload, value: selectedRow.detail.healthFileUploadSuccessCountLabel },
                      { key: "healthConfirm", label: copy.healthConfirm, value: selectedRow.detail.healthFileConfirmSuccessCountLabel },
                      { key: "healthAnalysis", label: copy.healthAnalysis, value: selectedRow.detail.healthAnalysisStartCountLabel },
                      { key: "healthSave", label: copy.healthSave, value: selectedRow.detail.healthRecordSaveSuccessCountLabel },
                      { key: "receiptExtract", label: copy.receiptExtract, value: selectedRow.detail.receiptExtractCountLabel },
                      { key: "receiptSave", label: copy.receiptSave, value: selectedRow.detail.receiptSaveSuccessCountLabel },
                      {
                        key: "receiptCategoryCreate",
                        label: copy.receiptCategoryCreate,
                        value: selectedRow.detail.receiptCategoryCreateSuccessCountLabel,
                      },
                      {
                        key: "receiptCategoryUpdate",
                        label: copy.receiptCategoryUpdate,
                        value: selectedRow.detail.receiptCategoryUpdateSuccessCountLabel,
                      },
                      {
                        key: "receiptCategoryDelete",
                        label: copy.receiptCategoryDelete,
                        value: selectedRow.detail.receiptCategoryDeleteSuccessCountLabel,
                      },
                      { key: "receiptTotal", label: copy.receiptTotal, value: selectedRow.detail.receiptFlowCountLabel },
                      { key: "totalSuccess", label: copy.totalSuccess, value: selectedRow.detail.totalSuccessCountLabel },
                      { key: "failures", label: copy.failures, value: selectedRow.detail.failureCountLabel },
                      { key: "topError", label: copy.topError, value: selectedRow.detail.topErrorCodeLabel },
                    ],
                  },
                  {
                    title: copy.drilldown,
                    items: [
                      {
                        key: "failedFlows",
                        label: copy.failedFlows,
                        value:
                          selectedRow.detail.drilldownTarget.failedFlows ? (
                            <AdminLocalizedLink to={selectedRow.detail.drilldownTarget.failedFlows}>{copy.openFailedFlows}</AdminLocalizedLink>
                          ) : (
                            "-"
                          ),
                      },
                      {
                        key: "userSummary",
                        label: copy.userSummary,
                        value:
                          selectedRow.detail.drilldownTarget.userSummary ? (
                            <AdminLocalizedLink to={selectedRow.detail.drilldownTarget.userSummary}>{copy.openUserSummary}</AdminLocalizedLink>
                          ) : (
                            "-"
                          ),
                      },
                      {
                        key: "releaseRegression",
                        label: copy.release,
                        value:
                          selectedRow.detail.drilldownTarget.releaseRegression ? (
                            <AdminLocalizedLink to={selectedRow.detail.drilldownTarget.releaseRegression}>{copy.openRegression}</AdminLocalizedLink>
                          ) : (
                            "-"
                          ),
                      },
                    ],
                  },
                  {
                    title: copy.freshness,
                    items: [
                      {
                        key: "sourceStatus",
                        label: copy.sourceStatus,
                        value: (
                          <AdminSourceStatusGrid
                            items={state.viewModel.sourceFreshness}
                            statusLabel={copy.sourceStatus}
                          />
                        ),
                      },
                    ],
                  },
                ]}
              />
            ) : (
              <AdminEmptyState
                title={copy.noSelectionTitle}
                body={copy.noSelectionBody}
              />
            )}
          </div>

          {renderBaselineContextSection(copy.baselineBodyActive)}
        </>
      ) : null}

      {renderFilterForm()}
    </section>
  );
}
